import { Action, Selector, State, StateContext } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ShipmentWaybillForeignTradeService } from './shipment-waybill-foreign-trade.service';
import {
  ShipmentWaybillForeignTradeLoad,
  ShipmentWaybillForeignTradeSetInitialState,
} from './shipment-waybill-foreign-trade.actions';
import { CustomHttpErrorResponse, NotificationService } from '../../../../../common';
import {
  ForeignTradeDocumentsFlowModel,
  ForeignTradeMainInformation,
  ForeignTradePendingDocument,
  WaybillForeignTrade,
} from '../../../../models/foreign-trade.model';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';

export interface ShipmentForeignTradeStateModel {
  foreignTrade: WaybillForeignTrade;
  count: number;
  loaded: boolean;
}

const initialState: ShipmentForeignTradeStateModel = {
  foreignTrade: null,
  count: 0,
  loaded: false,
};

@State<ShipmentForeignTradeStateModel>({
  name: 'shipmentWaybillForeignTrade',
  defaults: initialState,
})
@Injectable()
export class ShipmentWaybillForeignTradeState {
  constructor(
    private foreignTradeService: ShipmentWaybillForeignTradeService,
    private notificationService: NotificationService,
  ) {}

  @Selector()
  static getState(state: ShipmentForeignTradeStateModel): ShipmentForeignTradeStateModel {
    return state;
  }

  @Selector()
  static getLoaded(state: ShipmentForeignTradeStateModel): boolean {
    return state.loaded;
  }

  @Selector()
  static getForeignTrade(state: ShipmentForeignTradeStateModel): WaybillForeignTrade {
    return state.foreignTrade;
  }

  @Selector()
  static getMainInformation(state: ShipmentForeignTradeStateModel): ForeignTradeMainInformation {
    return !!state.foreignTrade ? state.foreignTrade.customsInformation : null;
  }

  @Selector()
  static getDocuments(state: ShipmentForeignTradeStateModel): ForeignTradePendingDocument[] {
    return !!state.foreignTrade ? state.foreignTrade.documents : [];
  }

  @Selector()
  static getDocumentsFlow(state: ShipmentForeignTradeStateModel): ForeignTradeDocumentsFlowModel[] {
    return !!state.foreignTrade ? state.foreignTrade.documentsFlow : [];
  }

  @Action(ShipmentWaybillForeignTradeLoad)
  getWaybillForeignTrade(
    ctx: StateContext<ShipmentForeignTradeStateModel>,
    { payload }: ShipmentWaybillForeignTradeLoad,
  ): Observable<void | WaybillForeignTrade | MatSnackBarRef<SimpleSnackBar>> {
    return this.foreignTradeService.getCustomsDocuments(payload).pipe(
      map((foreignTrade: WaybillForeignTrade) => {
        ctx.patchState({ foreignTrade, loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(ShipmentWaybillForeignTradeSetInitialState)
  setInitialState(ctx: StateContext<ShipmentForeignTradeStateModel>): void {
    ctx.setState(initialState);
  }
}
